import { useState, useRef, useEffect } from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Tag, Plus, X, Loader2, Palette, Lock, Upload, Sparkles, MousePointerClick, Ship, Keyboard, MessageCircle, Bug, Lightbulb, Megaphone, Heart, Flag, Star, LifeBuoy, Camera, Video, Mic, MessageSquare, Bell, Anchor, Mail, Check, ShieldCheck, Map, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const ICON_CHOICES = [
  { v: "ship", Icon: Ship },
  { v: "chat", Icon: MessageCircle },
  { v: "bug", Icon: Bug },
  { v: "lightbulb", Icon: Lightbulb },
  { v: "megaphone", Icon: Megaphone },
  { v: "heart", Icon: Heart },
  { v: "flag", Icon: Flag },
  { v: "star", Icon: Star },
  { v: "lifebuoy", Icon: LifeBuoy },
  { v: "sparkles", Icon: Sparkles },
];
const ICON_MAP = Object.fromEntries(ICON_CHOICES.map((c) => [c.v, c.Icon]));

const SUGGESTED_SUBJECTS = ["Bug", "UI/UX", "Performance", "Feature request", "Content/Copy", "Confusing", "Crash", "Mobile", "Accessibility", "Praise"];

// Mix a hex color toward white (0 = pure, 1 = white) → rgb() string. Mirrors widget.js tbMix.
function mixWhite(hex, ratio) {
  let c = (hex || "#FF6B35").replace("#", "");
  if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  let r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16);
  r = Math.round(r + (255 - r) * ratio); g = Math.round(g + (255 - g) * ratio); b = Math.round(b + (255 - b) * ratio);
  return `rgb(${r},${g},${b})`;
}
function textOn(rgb) {
  const m = rgb.match(/\d+/g) || [255, 255, 255];
  const lum = (0.299 * +m[0] + 0.587 * +m[1] + 0.114 * +m[2]) / 255;
  return lum > 0.62 ? "#1A1A1A" : "#fff";
}

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || "");
function hotkeyLabels(hk) {
  if (!hk) return [];
  return hk.split("+").map((p) => {
    if (p === "mod") return IS_MAC ? "⌘" : "Ctrl";
    if (p === "shift") return IS_MAC ? "⇧" : "Shift";
    if (p === "alt") return IS_MAC ? "⌥" : "Alt";
    return p.toUpperCase();
  });
}

export default function ProjectSettings({ projectId, plan, brandingEnabled, initialSubjects = [], initialBranding = {}, initialMode = "floating", initialPosition = "bottom-right", initialHotkey = "mod+shift+k", initialHotkeyEnabled = true, initialNotify = {}, initialPII = true, initialCountdown = true, whatsnewOn = true, roadmapOn = true }) {
  const [mode, setMode] = useState(initialMode || "floating");
  const [position, setPosition] = useState(initialPosition || "bottom-right");
  const [savingMode, setSavingMode] = useState(false);
  const [notifyMode, setNotifyMode] = useState(initialNotify.recipients || "owner");
  const [notifyUserIds, setNotifyUserIds] = useState(initialNotify.user_ids || []);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    apiClient.get("/org").then(({ data }) => setMembers(data.members || [])).catch(() => setMembers([]));
  }, []);

  // Inline "Saved ✓" flash for auto-saving controls (toggles/dropdowns save instantly).
  const [savedFlash, setSavedFlash] = useState("");
  const flashRef = useRef(null);
  const flashSaved = (id) => {
    setSavedFlash(id);
    clearTimeout(flashRef.current);
    flashRef.current = setTimeout(() => setSavedFlash(""), 1800);
  };
  const savedFlashEl = (id) => savedFlash === id
    ? <span data-testid={`saved-flash-${id}`} className="inline-flex items-center gap-1 text-xs font-800 text-grass"><Check className="h-3.5 w-3.5" /> Saved</span>
    : null;

  const persistNotify = async (mode, ids) => {
    try {
      await apiClient.put(`/projects/${projectId}/notifications`, { recipients: mode, user_ids: ids });
      flashSaved("notify");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save notifications");
    }
  };
  const changeNotifyMode = (m) => { setNotifyMode(m); persistNotify(m, notifyUserIds); };
  const toggleNotifyUser = (uid) =>
    setNotifyUserIds((prev) => {
      const next = prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid];
      persistNotify(notifyMode, next);
      return next;
    });
  const [subjects, setSubjects] = useState(initialSubjects);
  const [newSubject, setNewSubject] = useState("");
  const [savingSubjects, setSavingSubjects] = useState(false);

  const [guidedEnabled, setGuidedEnabled] = useState(false);
  const [guidedPrompts, setGuidedPrompts] = useState([]);
  const [guidedPersonas, setGuidedPersonas] = useState([]);
  const [newPersona, setNewPersona] = useState("");
  const [savingGuided, setSavingGuided] = useState(false);
  useEffect(() => {
    apiClient.get(`/projects/${projectId}/guided`).then(({ data }) => {
      setGuidedEnabled(!!data.guided_enabled);
      setGuidedPrompts(data.guided_prompts || []);
      setGuidedPersonas(data.guided_personas || []);
    }).catch(() => {});
  }, [projectId]);
  const saveGuided = async () => {
    setSavingGuided(true);
    try {
      const prompts = guidedPrompts.map((p) => p.trim()).filter(Boolean);
      await apiClient.put(`/projects/${projectId}/guided`, { guided_enabled: guidedEnabled, guided_prompts: prompts, guided_personas: guidedPersonas });
      toast.success("Guided submissions saved");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Could not save"); }
    finally { setSavingGuided(false); }
  };

  const [branding, setBranding] = useState({
    accent_color: initialBranding.accent_color || "#FF6B35",
    button_text: initialBranding.button_text || "Feedback",
    logo_url: initialBranding.logo_url || "",
    button_shape: initialBranding.button_shape || "pill",
    button_icon: initialBranding.button_icon || "",
    headline: initialBranding.headline || "",
    theme_capture: initialBranding.theme_capture || false,
    enabled: initialBranding.enabled !== false,
    brand_name: initialBranding.brand_name || "",
    label_screenshot: initialBranding.label_screenshot || "",
    label_screen_recording: initialBranding.label_screen_recording || "",
    label_voice: initialBranding.label_voice || "",
    label_text: initialBranding.label_text || "",
    send_text: initialBranding.send_text || "",
    sending_text: initialBranding.sending_text || "",
    thanks_text: initialBranding.thanks_text || "",
    consent_text: initialBranding.consent_text || "",
    hide_whatsnew: initialBranding.hide_whatsnew || false,
    allow_dismiss: initialBranding.allow_dismiss || false,
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savedBranding, setSavedBranding] = useState(null);
  const fileRef = useRef(null);
  const isBusiness = brandingEnabled !== undefined ? brandingEnabled : (plan === "business");

  // Snapshot the branding as-loaded so we can detect unsaved edits.
  useEffect(() => { setSavedBranding((s) => s ?? branding); }, [branding]);
  const brandingDirty = savedBranding !== null && JSON.stringify(branding) !== JSON.stringify(savedBranding);

  // Warn before closing/refreshing the tab with unsaved branding edits.
  useEffect(() => {
    if (!brandingDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [brandingDirty]);

  const [hotkeyEnabled, setHotkeyEnabled] = useState(initialHotkeyEnabled !== false);
  const [hotkey, setHotkey] = useState(initialHotkey || "mod+shift+k");
  const [recording, setRecording] = useState(false);

  const [piiRedaction, setPiiRedaction] = useState(initialPII !== false);
  const togglePii = async () => {
    const next = !piiRedaction;
    setPiiRedaction(next);
    try {
      await apiClient.put(`/projects/${projectId}/privacy`, { pii_redaction: next });
      flashSaved("privacy");
    } catch (e) {
      setPiiRedaction(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save privacy setting");
    }
  };

  const [countdown, setCountdown] = useState(initialCountdown !== false);
  const toggleCountdown = async () => {
    const next = !countdown;
    setCountdown(next);
    try {
      await apiClient.put(`/projects/${projectId}/capture-countdown`, { capture_countdown: next });
      flashSaved("countdown");
    } catch (e) {
      setCountdown(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save countdown setting");
    }
  };

  const [brandOpen, setBrandOpen] = useState(false);

  const onHotkeyKeyDown = (e) => {
    if (!recording) return;
    e.preventDefault();
    const k = e.key;
    if (k === "Escape") { setRecording(false); return; }
    if (["Control", "Shift", "Alt", "Meta"].includes(k)) return;
    const mods = [];
    if (e.ctrlKey || e.metaKey) mods.push("mod");
    if (e.shiftKey) mods.push("shift");
    if (e.altKey) mods.push("alt");
    if (!mods.length) { toast.error("Add at least one modifier (Ctrl/⌘, Shift or Alt)"); return; }
    const nhk = [...mods, k.toLowerCase()].join("+");
    setHotkey(nhk);
    setRecording(false);
    persistHotkey(nhk, hotkeyEnabled);
  };

  const persistHotkey = async (hk, enabled) => {
    try {
      await apiClient.put(`/projects/${projectId}/hotkey`, { hotkey: hk, enabled });
      flashSaved("hotkey");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const toggleHotkeyEnabled = () => {
    const next = !hotkeyEnabled;
    setHotkeyEnabled(next);
    persistHotkey(hotkey, next);
  };

  const addSubject = () => {
    const s = newSubject.trim();
    if (!s || subjects.includes(s)) { setNewSubject(""); return; }
    setSubjects([...subjects, s]);
    setNewSubject("");
  };

  const saveSubjects = async () => {
    setSavingSubjects(true);
    try {
      await apiClient.put(`/projects/${projectId}/subjects`, { subjects });
      toast.success("Subjects saved — testers can now pick these");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingSubjects(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await apiClient.post(`/projects/${projectId}/branding/logo`, form);
      setBranding((b) => ({ ...b, logo_url: data.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const saveBranding = async () => {
    setSavingBranding(true);
    try {
      await apiClient.put(`/projects/${projectId}/branding`, branding);
      setSavedBranding(branding);
      toast.success("Branding saved — your widget is now white-labeled 🎨");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingBranding(false);
    }
  };

  const persistBranding = async (next) => {
    setBranding(next);
    try {
      await apiClient.put(`/projects/${projectId}/branding`, next);
      setSavedBranding(next);
      flashSaved("branding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeLogo = async () => {
    const next = { ...branding, logo_url: "" };
    setBranding(next);
    if (fileRef.current) fileRef.current.value = "";
    try {
      await apiClient.put(`/projects/${projectId}/branding`, next);
      setSavedBranding(next);
      toast.success("Logo removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const backendOrigin = (process.env.REACT_APP_BACKEND_URL || "");
  const logoSrc = branding.logo_url ? (branding.logo_url.startsWith("http") ? branding.logo_url : backendOrigin + branding.logo_url) : "";

  const saveMode = async (m) => {
    setMode(m);
    setSavingMode(true);
    try {
      await apiClient.put(`/projects/${projectId}/widget-mode`, { mode: m, position });
      toast.success(m === "manual" ? "Switched to manual trigger mode" : "Floating button enabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingMode(false);
    }
  };

  const savePosition = async (p) => {
    setPosition(p);
    try {
      await apiClient.put(`/projects/${projectId}/widget-mode`, { mode, position: p });
      toast.success("Button position saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="project-settings">
      {/* Widget display mode */}
      <div className="nb-card p-6">
        <h3 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><MousePointerClick className="h-5 w-5 text-orange" /> Widget display</h3>
        <p className="text-sm text-ink/60 mb-4 font-600">Show the floating button, or hide it and open Tugboat from your own UI (a menu item, settings page, anywhere).</p>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="widget-mode">
          {[
            { v: "floating", title: "Floating button", desc: "A draggable button sits on the page." },
            { v: "manual", title: "Manual trigger", desc: "No button — you open it via code." },
          ].map((o) => (
            <button key={o.v} onClick={() => saveMode(o.v)} disabled={savingMode} data-testid={`widget-mode-${o.v}`}
              className={`text-left p-4 border-2 border-ink rounded-2xl transition-all ${mode === o.v ? "bg-sunny shadow-nb-sm" : "bg-white hover:bg-sand"}`}>
              <p className="font-800 text-sm">{o.title}</p>
              <p className="text-xs text-ink/60 font-600 mt-0.5">{o.desc}</p>
            </button>
          ))}
        </div>
        {mode === "floating" && (
          <div className="mt-4" data-testid="button-position">
            <p className="label-overline mb-2">Default button position</p>
            <p className="text-xs text-ink/55 font-600 mb-3">Where the button first appears. Testers can still drag it anywhere.</p>
            <div className="grid grid-cols-2 gap-2 max-w-xs">
              {[
                { v: "top-left", label: "Top left" },
                { v: "top-right", label: "Top right" },
                { v: "bottom-left", label: "Bottom left" },
                { v: "bottom-right", label: "Bottom right" },
              ].map((o) => (
                <button key={o.v} onClick={() => savePosition(o.v)} data-testid={`position-${o.v}`}
                  className={`relative p-3 h-16 border-2 border-ink rounded-2xl transition-all ${position === o.v ? "bg-sunny shadow-nb-sm" : "bg-white hover:bg-sand"}`}>
                  <span className={`absolute h-3 w-3 rounded-full bg-orange border-2 border-ink ${o.v.includes("top") ? "top-2" : "bottom-2"} ${o.v.includes("left") ? "left-2" : "right-2"}`} />
                  <span className="text-[11px] font-700 text-ink/70">{o.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {mode === "manual" && (
          <div className="mt-4">
            <p className="label-overline mb-1.5">Trigger from your own button</p>
            <pre className="bg-ink text-sunny text-xs p-3 rounded-xl overflow-x-auto font-mono border-2 border-ink" data-testid="manual-snippet">{`<button onclick="Tugboat.open()">Send feedback</button>`}</pre>
          </div>
        )}
        <div className="mt-4 pt-4 border-t-2 border-dashed border-ink/15 flex items-center justify-between gap-4">
          <div>
            <p className="font-700 text-sm flex items-center gap-2">Capture countdown {savedFlashEl("countdown")}</p>
            <p className="text-[11px] text-ink/55 font-700">Show a 3&middot;2&middot;1 countdown before a screenshot, screen or voice capture starts so testers can get ready.</p>
          </div>
          <button type="button" onClick={toggleCountdown} data-testid="capture-countdown-toggle" aria-pressed={countdown}
            className={`relative h-7 w-12 rounded-full border-2 border-ink transition-colors shrink-0 ${countdown ? "bg-grass" : "bg-white"}`}>
            <span className={`absolute top-[2px] h-5 w-5 rounded-full border-2 border-ink bg-white transition-all ${countdown ? "left-[22px]" : "left-[2px]"}`} />
          </button>
        </div>
      </div>

      {/* Email notifications */}
      <div className="nb-card p-6" data-testid="notifications-card">
        <h3 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><Mail className="h-5 w-5 text-orange" /> Email notifications</h3>
        <p className="text-sm text-ink/60 mb-4 font-600">Choose who gets an email when new feedback arrives on this project.</p>
        <div className="space-y-2">
          {[
            { v: "owner", label: "Project owner only", desc: "Just the person who owns this project." },
            { v: "members", label: "All members with access", desc: "Everyone on your team who can see this project." },
            { v: "custom", label: "Specific people", desc: "Pick exactly who should be notified." },
          ].map((o) => (
            <label key={o.v} data-testid={`notify-mode-${o.v}`}
              className={`flex items-start gap-3 p-3 rounded-2xl border-2 cursor-pointer transition-colors ${notifyMode === o.v ? "border-ink bg-sunny" : "border-ink/15 hover:border-ink/40"}`}>
              <input type="radio" name="notify-mode" checked={notifyMode === o.v} onChange={() => changeNotifyMode(o.v)} className="mt-1 h-4 w-4 accent-orange" />
              <span>
                <span className="font-700 text-sm block">{o.label}</span>
                <span className="text-xs text-ink/55 font-600">{o.desc}</span>
              </span>
            </label>
          ))}
        </div>
        {notifyMode === "custom" && (
          <div className="mt-3 border-2 border-ink/15 rounded-2xl p-3 space-y-1.5 max-h-56 overflow-y-auto" data-testid="notify-members-list">
            {members.length === 0 ? (
              <p className="text-sm text-ink/40 font-600 py-2 text-center">No teammates yet — invite people from the Team page.</p>
            ) : members.map((m) => (
              <label key={m.user_id} data-testid={`notify-user-${m.user_id}`}
                className="flex items-center gap-3 px-2 py-1.5 rounded-xl hover:bg-sand cursor-pointer">
                <input type="checkbox" checked={notifyUserIds.includes(m.user_id)} onChange={() => toggleNotifyUser(m.user_id)} className="h-4 w-4 accent-orange" />
                <span className="text-sm font-700">{m.name || m.email}</span>
                <span className="text-xs text-ink/50 font-600 truncate">{m.email}</span>
              </label>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3 mt-4 h-6" data-testid="notify-autosave-row">{savedFlashEl("notify")}</div>
        <p className="text-[11px] text-ink/45 font-600 mt-1">Changes save automatically. Teammates can turn off feedback emails for themselves on the Team page.</p>
      </div>

      {/* Subjects */}
      <div className="nb-card p-6">
        <h3 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><Tag className="h-5 w-5 text-orange" /> Subjects & topics</h3>
        <p className="text-sm text-ink/60 mb-4 font-600">Define the topics testers choose from when sending feedback — so it lands pre-categorized.</p>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="subjects-list">
          {subjects.length === 0 && <span className="text-sm text-ink/40 font-600">No subjects yet.</span>}
          {subjects.map((s) => (
            <span key={s} className="flex items-center gap-1.5 bg-sunny border-2 border-ink rounded-full pl-3 pr-2 py-1 text-sm font-700">
              {s}
              <button onClick={() => setSubjects(subjects.filter((x) => x !== s))} data-testid={`remove-subject-${s}`} className="hover:text-danger"><X className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="subject-input" value={newSubject} onChange={(e) => setNewSubject(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSubject(); } }}
            className="nb-input flex-1" placeholder="e.g. Bug, UI/UX, Performance" />
          <button onClick={addSubject} data-testid="add-subject" className="nb-btn bg-white px-4 py-2.5 text-sm shrink-0"><Plus className="h-4 w-4" /> Add</button>
        </div>
        {SUGGESTED_SUBJECTS.some((s) => !subjects.includes(s)) && (
          <div className="mt-3" data-testid="suggested-subjects">
            <p className="text-xs text-ink/50 font-700 mb-2">Suggested — tap to add:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_SUBJECTS.filter((s) => !subjects.includes(s)).map((s) => (
                <button key={s} onClick={() => setSubjects([...subjects, s])} data-testid={`suggest-subject-${s}`}
                  className="flex items-center gap-1 bg-white border-2 border-dashed border-ink/30 rounded-full px-3 py-1 text-sm font-700 text-ink/70 hover:border-ink hover:text-ink hover:border-solid transition-colors">
                  <Plus className="h-3 w-3" /> {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <button onClick={saveSubjects} disabled={savingSubjects} data-testid="save-subjects" className="nb-btn bg-orange text-white px-5 py-2.5 text-sm mt-4 disabled:opacity-60">
          {savingSubjects ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save subjects"}
        </button>
      </div>

      {/* Guided submissions */}
      <div className="nb-card p-6" data-testid="guided-card">
        <h3 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><MessageSquare className="h-5 w-5 text-orange" /> Guided submissions</h3>
        <p className="text-sm text-ink/60 mb-4 font-600">Coach non-technical testers to give useful context. When on, the note box is replaced by short prompts so you get consistent, readable reports instead of “it’s broken.”</p>

        <div className="flex items-center justify-between bg-sand border-2 border-ink rounded-2xl p-3.5 mb-4">
          <div>
            <p className="font-800 text-sm">Enable guided prompts</p>
            <p className="text-xs text-ink/60 font-600">Off by default — the standard free-text note is used when disabled.</p>
          </div>
          <button type="button" onClick={() => setGuidedEnabled((v) => !v)} data-testid="guided-toggle" aria-pressed={guidedEnabled}
            className={`relative h-7 w-12 rounded-full border-2 border-ink transition-colors shrink-0 ${guidedEnabled ? "bg-grass" : "bg-white"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full border-2 border-ink bg-white transition-all ${guidedEnabled ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>

        {guidedEnabled && (
          <>
            <p className="label-overline mb-2">Prompts (each becomes its own field)</p>
            <div className="space-y-2 mb-3" data-testid="guided-prompts-list">
              {guidedPrompts.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <input value={p} data-testid={`guided-prompt-${i}`}
                    onChange={(e) => setGuidedPrompts(guidedPrompts.map((x, j) => (j === i ? e.target.value : x)))}
                    className="nb-input flex-1" placeholder="e.g. What did you expect to happen?" />
                  <button onClick={() => setGuidedPrompts(guidedPrompts.filter((_, j) => j !== i))} data-testid={`remove-guided-prompt-${i}`}
                    className="nb-btn bg-white px-3 shrink-0"><X className="h-4 w-4" /></button>
                </div>
              ))}
              {guidedPrompts.length < 6 && (
                <button onClick={() => setGuidedPrompts([...guidedPrompts, ""])} data-testid="add-guided-prompt"
                  className="flex items-center gap-1 text-sm font-700 text-ink/70 hover:text-ink"><Plus className="h-4 w-4" /> Add prompt</button>
              )}
            </div>

            <p className="label-overline mb-2">Personas (optional — shows an “I’m reporting as…” dropdown)</p>
            <div className="flex flex-wrap gap-2 mb-2" data-testid="guided-personas-list">
              {guidedPersonas.length === 0 && <span className="text-sm text-ink/40 font-600">No personas — dropdown hidden.</span>}
              {guidedPersonas.map((p) => (
                <span key={p} className="flex items-center gap-1.5 bg-teal border-2 border-ink rounded-full pl-3 pr-2 py-1 text-sm font-700">
                  {p}
                  <button onClick={() => setGuidedPersonas(guidedPersonas.filter((x) => x !== p))} data-testid={`remove-persona-${p}`} className="hover:text-danger"><X className="h-3.5 w-3.5" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mb-4">
              <input value={newPersona} data-testid="persona-input"
                onChange={(e) => setNewPersona(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const s = newPersona.trim(); if (s && !guidedPersonas.includes(s)) setGuidedPersonas([...guidedPersonas, s]); setNewPersona(""); } }}
                className="nb-input flex-1" placeholder="e.g. End user, Admin, Developer, QA" />
              <button onClick={() => { const s = newPersona.trim(); if (s && !guidedPersonas.includes(s)) setGuidedPersonas([...guidedPersonas, s]); setNewPersona(""); }}
                data-testid="add-persona" className="nb-btn bg-white px-4 py-2.5 text-sm shrink-0"><Plus className="h-4 w-4" /> Add</button>
            </div>
          </>
        )}
        <button onClick={saveGuided} disabled={savingGuided} data-testid="save-guided" className="nb-btn bg-orange text-white px-5 py-2.5 text-sm disabled:opacity-60">
          {savingGuided ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save guided settings"}
        </button>
      </div>

      {/* Keyboard shortcut */}
      <div className="nb-card p-6" data-testid="hotkey-card">
        <h3 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><Keyboard className="h-5 w-5 text-orange" /> Keyboard shortcut</h3>
        <p className="text-sm text-ink/60 mb-4 font-600">Let testers open Tugboat instantly from anywhere on the page — even while typing. Available on every plan.</p>

        <div className="flex items-center justify-between bg-sand border-2 border-ink rounded-2xl p-3.5 mb-4">
          <div>
            <p className="font-800 text-sm">Enable shortcut</p>
            <p className="text-xs text-ink/60 font-600">Turn the keyboard shortcut on or off for this project.</p>
          </div>
          <button type="button" onClick={toggleHotkeyEnabled} data-testid="hotkey-toggle" aria-pressed={hotkeyEnabled}
            className={`relative h-7 w-12 rounded-full border-2 border-ink transition-colors shrink-0 ${hotkeyEnabled ? "bg-grass" : "bg-white"}`}>
            <span className={`absolute top-[2px] h-5 w-5 rounded-full border-2 border-ink bg-white transition-all ${hotkeyEnabled ? "left-[22px]" : "left-[2px]"}`} />
          </button>
        </div>

        {hotkeyEnabled && (
          <div className="space-y-3 mb-4" data-testid="hotkey-config">
            <label className="label-overline block">Shortcut</label>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5" data-testid="hotkey-display">
                {hotkeyLabels(hotkey).map((k, i) => (
                  <kbd key={i} className="font-mono text-sm bg-sunny border-2 border-ink rounded-md px-2 py-1 shadow-nb-sm">{k}</kbd>
                ))}
              </div>
              <button type="button" data-testid="hotkey-record"
                onClick={() => setRecording(true)} onKeyDown={onHotkeyKeyDown} onBlur={() => setRecording(false)}
                className={`nb-btn px-4 py-2 text-sm ${recording ? "bg-danger text-white" : "bg-white"}`}>
                {recording ? "Press keys… (Esc to cancel)" : "Record shortcut"}
              </button>
            </div>
            <p className="text-[11px] text-ink/50 font-700">Use at least one modifier (Ctrl/⌘, Shift or Alt) plus a key. Ctrl &amp; ⌘ are interchangeable across Windows and Mac.</p>
          </div>
        )}
        <div className="flex items-center gap-3 h-6" data-testid="hotkey-autosave-row">{savedFlashEl("hotkey")}<span className="text-[11px] text-ink/45 font-600">Changes save automatically.</span></div>
      </div>

      {/* Privacy / PII redaction */}
      <div className="nb-card p-6" data-testid="privacy-card">
        <h3 className="font-display font-700 text-xl mb-1 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-orange" /> Privacy & PII redaction</h3>
        <p className="text-sm text-ink/60 mb-4 font-600">For session replays, automatically scrub emails, phone numbers and card-like numbers from recorded text. All form inputs are always masked.</p>
        <div className="flex items-center justify-between bg-sand border-2 border-ink rounded-2xl p-3.5">
          <div>
            <p className="font-800 text-sm">Auto-redact PII in session replays</p>
            <p className="text-xs text-ink/60 font-600">Recommended. You can also opt out specific regions with <code className="font-mono">data-tugboat-block</code> (skip) or <code className="font-mono">data-tugboat-mask</code> (mask) in your markup.</p>
          </div>
          <button type="button" onClick={togglePii} data-testid="pii-toggle" aria-pressed={piiRedaction}
            className={`relative h-7 w-12 rounded-full border-2 border-ink transition-colors shrink-0 ${piiRedaction ? "bg-grass" : "bg-white"}`}>
            <span className={`absolute top-[2px] h-5 w-5 rounded-full border-2 border-ink bg-white transition-all ${piiRedaction ? "left-[22px]" : "left-[2px]"}`} />
          </button>
        </div>
        <div className="flex items-center gap-3 mt-3 h-6" data-testid="privacy-autosave-row">{savedFlashEl("privacy")}<span className="text-[11px] text-ink/45 font-600">Changes save automatically.</span></div>
      </div>

      {/* Branding */}
      <div className="nb-card p-6">
        <button type="button" onClick={() => setBrandOpen((v) => !v)} data-testid="branding-collapse-toggle" className="w-full flex items-center justify-between gap-2 text-left">
          <h3 className="font-display font-700 text-xl flex items-center gap-2"><Palette className="h-5 w-5 text-orange" /> Custom branding</h3>
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1 text-[10px] font-800 uppercase tracking-wide px-2.5 py-1 rounded-full border-2 border-ink ${isBusiness ? "bg-grass text-white" : "bg-white text-ink/50"}`}>
              <Sparkles className="h-3 w-3" /> Business
            </span>
            <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${brandOpen ? "rotate-180" : ""}`} />
          </div>
        </button>
        <p className="text-sm text-ink/60 mt-1 mb-4 font-600">White-label the widget with your client's logo and colors.{!brandOpen && " Click to expand."}</p>
        {brandOpen && (<>

        {!isBusiness ? (
          <div className="bg-sand border-2 border-dashed border-ink/30 rounded-2xl p-8 text-center" data-testid="branding-locked">
            <Lock className="h-8 w-8 text-ink/30 mx-auto mb-3" />
            <p className="font-700 mb-1">Custom branding is a Business feature</p>
            <p className="text-sm text-ink/60 font-600">Upgrade to white-label the widget with your own logo, colors and button text.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="branding-form">
            <div>
              <label className="label-overline block mb-1.5">Widget logo</label>
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-2xl border-2 border-ink bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {logoSrc ? <img src={logoSrc} alt="logo" className="h-full w-full object-contain" /> : <span className="text-xs text-ink/40 font-700">None</span>}
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="nb-btn bg-white px-4 py-2.5 text-sm">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> {logoSrc ? "Replace" : "Upload logo"}</>}
                </button>
                {logoSrc && (
                  <button onClick={removeLogo} data-testid="remove-logo" className="nb-btn bg-white text-danger px-4 py-2.5 text-sm">
                    <X className="h-4 w-4" /> Remove
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label-overline block mb-1.5">Accent color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={branding.accent_color} onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                    className="color-swatch h-11 w-12 border-2 border-ink rounded-xl cursor-pointer" data-testid="accent-color-input" />
                  <input value={branding.accent_color} onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })} className="nb-input font-mono flex-1" />
                </div>
              </div>
              <div>
                <label className="label-overline block mb-1.5">Button text</label>
                <input value={branding.button_text} onChange={(e) => setBranding({ ...branding, button_text: e.target.value })} className="nb-input" data-testid="button-text-input" placeholder="Feedback" />
              </div>
            </div>
            {/* Match capture buttons to brand */}
            <div className="flex items-center justify-between gap-3 bg-sand border-2 border-ink/15 rounded-2xl px-4 py-3">
              <div>
                <p className="font-700 text-sm">Match capture buttons to brand</p>
                <p className="text-[11px] text-ink/55 font-700">Tint all four capture tiles (screenshot, record, voice, note) from your accent color instead of the default multicolor.</p>
              </div>
              <button type="button" onClick={() => persistBranding({ ...branding, theme_capture: !branding.theme_capture })} data-testid="theme-capture-toggle" aria-pressed={!!branding.theme_capture}
                className={`relative h-7 w-12 rounded-full border-2 border-ink transition-colors shrink-0 ${branding.theme_capture ? "bg-grass" : "bg-white"}`}>
                <span className={`absolute top-[2px] h-5 w-5 rounded-full border-2 border-ink bg-white transition-all ${branding.theme_capture ? "left-[22px]" : "left-[2px]"}`} />
              </button>
            </div>
            {/* Panel headline */}
            <div>
              <label className="label-overline block mb-1.5">Panel headline</label>
              <input value={branding.headline} onChange={(e) => setBranding({ ...branding, headline: e.target.value.slice(0, 60) })} className="nb-input" data-testid="headline-input" placeholder="Ahoy! Found a bug?" maxLength={60} />
              <p className="text-[11px] text-ink/50 font-700 mt-1">Shown at the top of the widget when a tester opens it. Leave blank for the default.</p>
            </div>

            {/* White-label text */}
            <div className="border-2 border-ink/15 rounded-2xl p-4 space-y-3" data-testid="whitelabel-text">
              <p className="label-overline">Widget text (white-label)</p>
              <p className="text-[11px] text-ink/50 font-700 -mt-1">Every label below falls back to a sensible default if left blank. Set your brand name to remove all references to Tugboat.</p>
              <div>
                <label className="text-xs font-800 block mb-1">Brand name</label>
                <input value={branding.brand_name || ""} onChange={(e) => setBranding({ ...branding, brand_name: e.target.value.slice(0, 40) })} className="nb-input" data-testid="brand-name-input" placeholder="Acme Inc." maxLength={40} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-xs font-800 block mb-1">Screenshot label</label><input value={branding.label_screenshot || ""} onChange={(e) => setBranding({ ...branding, label_screenshot: e.target.value.slice(0, 30) })} className="nb-input" placeholder="Screenshot" data-testid="label-screenshot-input" /></div>
                <div><label className="text-xs font-800 block mb-1">Screen recording label</label><input value={branding.label_screen_recording || ""} onChange={(e) => setBranding({ ...branding, label_screen_recording: e.target.value.slice(0, 30) })} className="nb-input" placeholder="Record screen" data-testid="label-screenrec-input" /></div>
                <div><label className="text-xs font-800 block mb-1">Voice label</label><input value={branding.label_voice || ""} onChange={(e) => setBranding({ ...branding, label_voice: e.target.value.slice(0, 30) })} className="nb-input" placeholder="Voice note" data-testid="label-voice-input" /></div>
                <div><label className="text-xs font-800 block mb-1">Text note label</label><input value={branding.label_text || ""} onChange={(e) => setBranding({ ...branding, label_text: e.target.value.slice(0, 30) })} className="nb-input" placeholder="Write note" data-testid="label-text-input" /></div>
                <div><label className="text-xs font-800 block mb-1">Send button</label><input value={branding.send_text || ""} onChange={(e) => setBranding({ ...branding, send_text: e.target.value.slice(0, 30) })} className="nb-input" placeholder="Send feedback" data-testid="send-text-input" /></div>
                <div><label className="text-xs font-800 block mb-1">Sending message</label><input value={branding.sending_text || ""} onChange={(e) => setBranding({ ...branding, sending_text: e.target.value.slice(0, 40) })} className="nb-input" placeholder="Hauling it in…" data-testid="sending-text-input" /></div>
              </div>
              <div><label className="text-xs font-800 block mb-1">Thank-you message</label><input value={branding.thanks_text || ""} onChange={(e) => setBranding({ ...branding, thanks_text: e.target.value.slice(0, 120) })} className="nb-input" placeholder="⚓ Feedback sent. Thanks, matey!" data-testid="thanks-text-input" /></div>
              <div><label className="text-xs font-800 block mb-1">Consent line</label><input value={branding.consent_text || ""} onChange={(e) => setBranding({ ...branding, consent_text: e.target.value.slice(0, 200) })} className="nb-input" placeholder="By sending, you agree to share this feedback with your team." data-testid="consent-text-input" /></div>
              <div className="flex items-center justify-between bg-sand border-2 border-ink rounded-xl p-3">
                <div><p className="font-800 text-sm">Show a close (×) button on the widget</p><p className="text-[11px] text-ink/60 font-600">Lets testers hide the feedback button; they can bring it back anytime from a small “Feedback” tab.</p></div>
                <button type="button" onClick={() => persistBranding({ ...branding, allow_dismiss: !branding.allow_dismiss })} data-testid="allow-dismiss-toggle" aria-pressed={!!branding.allow_dismiss}
                  className={`relative h-7 w-12 rounded-full border-2 border-ink transition-colors shrink-0 ${branding.allow_dismiss ? "bg-grass" : "bg-white"}`}>
                  <span className={`absolute top-[2px] h-5 w-5 rounded-full border-2 border-ink bg-white transition-all ${branding.allow_dismiss ? "left-[22px]" : "left-[2px]"}`} />
                </button>
              </div>
            </div>

            {/* Button shape */}
            <div>
              <label className="label-overline block mb-1.5">Button shape</label>
              <div className="grid grid-cols-3 gap-2" data-testid="button-shape-options">
                {[
                  { v: "pill", label: "Pill", hint: "Icon + text" },
                  { v: "circle", label: "Circle", hint: "Logo only" },
                  { v: "square", label: "Rounded", hint: "Logo only" },
                ].map((s) => (
                  <button key={s.v} type="button" onClick={() => persistBranding({ ...branding, button_shape: s.v })}
                    data-testid={`shape-${s.v}`}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all ${branding.button_shape === s.v ? "border-ink bg-sunny shadow-nb-sm" : "border-ink/20 bg-white hover:border-ink/50"}`}>
                    <span
                      className="bg-ink"
                      style={{
                        backgroundColor: branding.accent_color,
                        width: s.v === "pill" ? 44 : 26, height: 26,
                        borderRadius: s.v === "circle" ? 999 : s.v === "square" ? 8 : 999,
                        border: "2px solid #1A1A1A",
                      }}
                    />
                    <span className="text-xs font-800">{s.label}</span>
                    <span className="text-[10px] text-ink/50 font-700">{s.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Button icon (preset) */}
            <div>
              <label className="label-overline block mb-1.5">Button icon</label>
              <p className="text-xs text-ink/55 font-600 mb-2">Pick a preset icon for the floating button. {logoSrc ? "Your uploaded logo overrides this." : ""}</p>
              <div className="grid grid-cols-5 gap-2" data-testid="button-icon-options">
                {ICON_CHOICES.map(({ v, Icon }) => {
                  const active = (branding.button_icon || "ship") === v;
                  return (
                    <button key={v} type="button" onClick={() => persistBranding({ ...branding, button_icon: v })}
                      data-testid={`icon-${v}`} title={v}
                      className={`flex items-center justify-center p-3 rounded-2xl border-2 transition-all ${active ? "border-ink bg-sunny shadow-nb-sm" : "border-ink/20 bg-white hover:border-ink/50"}`}>
                      <Icon className="h-5 w-5" />
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Preview */}
            <div>
              <label className="label-overline block mb-1.5">Preview</label>
              <div className="bg-sand border-2 border-ink rounded-2xl p-4" data-testid="branding-preview">
                {(() => {
                  const ac = branding.accent_color || "#FF6B35";
                  const themed = !!branding.theme_capture;
                  const PreviewIcon = ICON_MAP[branding.button_icon || "ship"] || Ship;
                  const headBg = themed ? mixWhite(ac, 0.72) : "#FFE66D";
                  const tiles = [
                    { Icon: Camera, label: branding.label_screenshot || "Screenshot", def: "#4ECDC4", r: 0 },
                    { Icon: Video, label: branding.label_screen_recording || "Record screen", def: "#FFE66D", r: 0.2 },
                    { Icon: Mic, label: branding.label_voice || "Voice note", def: ac, r: 0.4 },
                    { Icon: MessageSquare, label: branding.label_text || "Write note", def: "#FFFFFF", r: 0.62 },
                  ];
                  return (
                    <div className="flex flex-col items-center gap-4">
                      {/* Mini widget panel */}
                      <div className="w-[280px] bg-cream border-2 border-ink rounded-2xl overflow-hidden shadow-nb-sm" data-testid="widget-panel-preview">
                        <div className="flex items-center justify-between px-3.5 py-2.5 border-b-2 border-ink" style={{ backgroundColor: headBg, color: textOn(headBg) }}>
                          <span className="flex items-center gap-1.5 font-700 text-sm" style={{ fontFamily: "Fredoka, Nunito, sans-serif" }}>
                            <Anchor className="h-3.5 w-3.5" /> {branding.headline || "Ahoy! Found a bug?"}
                          </span>
                          <span className="h-5 w-5 flex items-center justify-center bg-white border-2 border-ink rounded-md"><X className="h-3 w-3" /></span>
                        </div>
                        <div className="p-3">
                          <div className="grid grid-cols-2 gap-2">
                            {tiles.map((t, i) => {
                              const bg = themed ? mixWhite(ac, t.r) : t.def;
                              const fg = themed ? textOn(bg) : (i === 2 ? "#fff" : "#1A1A1A");
                              return (
                                <div key={i} className="flex flex-col items-start gap-1.5 p-2.5 rounded-xl border-2 border-ink" style={{ backgroundColor: bg, color: fg }}>
                                  <t.Icon className="h-4 w-4" />
                                  <span className="text-[11px] font-800">{t.label}</span>
                                </div>
                              );
                            })}
                          </div>
                          {whatsnewOn && (
                            <div className="mt-2 flex items-center justify-center gap-1.5 py-2 rounded-xl border-2 border-ink text-white text-[11px] font-800" style={{ backgroundColor: ac }} data-testid="preview-whatsnew">
                              <Bell className="h-3.5 w-3.5" /> What's new
                            </div>
                          )}
                          {roadmapOn && (
                            <div className="mt-2 flex items-center justify-center gap-1.5 py-2 rounded-xl border-2 border-ink bg-white text-ink text-[11px] font-800" data-testid="preview-roadmap">
                              <Map className="h-3.5 w-3.5" /> Roadmap
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Floating button */}
                      {branding.button_shape === "pill" ? (
                        <span className="flex items-center gap-2 text-white pl-3 pr-4 py-3 rounded-full border-2 border-ink shadow-nb-sm font-800 text-sm" style={{ backgroundColor: ac }}>
                          {logoSrc ? <img src={logoSrc} alt="" className="h-5 w-5 object-contain" /> : <PreviewIcon className="h-5 w-5" />}
                          {branding.button_text || "Feedback"}
                        </span>
                      ) : (
                        <span className="flex items-center justify-center text-white border-2 border-ink shadow-nb-sm"
                          style={{ backgroundColor: ac, width: 52, height: 52, borderRadius: branding.button_shape === "circle" ? 999 : 16 }}>
                          {logoSrc ? <img src={logoSrc} alt="" className="h-6 w-6 object-contain" /> : <PreviewIcon className="h-6 w-6" />}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap mt-1">
              <button onClick={saveBranding} disabled={savingBranding || !brandingDirty} data-testid="save-branding"
                className={`nb-btn text-white px-5 py-2.5 text-sm disabled:opacity-60 ${brandingDirty ? "bg-orange" : "bg-ink/40"}`}>
                {savingBranding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save branding"}
              </button>
              {brandingDirty && (
                <span className="flex items-center gap-1.5 text-xs font-800 text-orange" data-testid="branding-unsaved-indicator">
                  <span className="h-2 w-2 rounded-full bg-orange animate-pulse" /> Unsaved changes
                </span>
              )}
              {savedFlashEl("branding")}
              <span className="text-[11px] text-ink/45 font-600 basis-full">Toggles, shape & icon save instantly. Text fields save with the button.</span>
            </div>
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}
